import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Hospital, Heart, ArrowRight, Shield, Users, Plus } from 'lucide-react';
import { motion } from 'framer-motion';

const Register = () => {
  const navigate = useNavigate();

  const handleDonorSelect = () => {
    navigate('/register-donor', { state: { roles: ['donor', 'requestor'] } });
  };

  const handleRequestorSelect = () => {
    navigate('/register-donor', { state: { roles: ['requestor', 'donor'] } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center py-6 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.5
            }}
            className="absolute rounded-full bg-red-500 blur-2xl opacity-10"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${200 + i * 50}px`,
              height: `${200 + i * 50}px`,
            }}
          />
        ))}
        {/* Floating Blood Cells */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`cell-${i}`}
            initial={{ y: "120vh" }}
            animate={{
              y: "-20vh",
              rotate: 360,
            }}
            transition={{
              duration: 15 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 20
            }}
            className="absolute w-12 h-12 opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
            }}
          >
            <div className="w-full h-full bg-red-500 rounded-full opacity-60" />
            <div className="absolute top-2 left-2 w-3 h-3 bg-white rounded-full opacity-40" />
          </motion.div>
        ))}
      </div>

      <div className="w-full max-w-2xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Role</h2>
          <p className="text-sm text-gray-600 max-w-lg mx-auto">
            Select how you'd like to contribute to our life-saving mission
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Donor Registration Option */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.01, y: -2 }}
            className="group"
          >
            <motion.button
              onClick={handleDonorSelect}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-red-200 p-5 hover:border-red-400 hover:shadow-xl transition-all duration-300 text-left group h-full"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <Heart className="w-5 h-5 text-red-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-red-400 group-hover:text-red-600 transition-colors" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">Blood Donor</h3>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed line-clamp-2">
                Register as a blood donor and help save lives by donating blood to those in need.
              </p>

              <div className="space-y-1.5">
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                  <span>Save up to 3 lives per donation</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                  <span>Get notified of urgent requests</span>
                </div>
              </div>
            </motion.button>
          </motion.div>

          {/* Requestor Registration Option */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.01, y: -2 }}
            className="group"
          >
            <motion.button
              onClick={handleRequestorSelect}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-blue-200 p-5 hover:border-blue-400 hover:shadow-xl transition-all duration-300 text-left group h-full"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Hospital className="w-5 h-5 text-blue-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 group-hover:text-blue-600 transition-colors" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">Blood Requester</h3>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed line-clamp-2">
                Register to request blood donations for patients in need.
              </p>

              <div className="space-y-1.5">
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  <span>Request blood for patients</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  <span>Connect with verified donors</span>
                </div>
              </div>
            </motion.button>
          </motion.div>
        </div>

        {/* Login Link Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md border border-gray-200 p-4 max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <span className="text-xs text-gray-500 font-medium">Already have an account?</span>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-red-600 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-600 hover:text-white transition-all duration-200"
              >
                <User className="w-3 h-3" />
                Sign in
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;